import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Settings, Shield, BarChart, Target, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { CookieConsentData } from '@/utils/cookieConsent';

interface CookieConsentBannerProps {
  position?: 'bottom' | 'top' | 'bottom-left' | 'bottom-right';
  compact?: boolean;
}

export function CookieConsentBanner({ 
  position = 'bottom-right', 
  compact = true 
}: CookieConsentBannerProps) {
  const { t } = useTranslation();
  const {
    showBanner,
    acceptAll,
    acceptNecessary,
    saveCustomConsent,
    cookieDescriptions
  } = useCookieConsent();

  const [showDetails, setShowDetails] = useState(false);
  const [customConsent, setCustomConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: true
  });

  if (!showBanner) return null;

  const positionClasses = {
    'bottom': 'bottom-4 left-1/2 transform -translate-x-1/2 max-w-4xl',
    'top': 'top-4 left-1/2 transform -translate-x-1/2 max-w-4xl',
    'bottom-left': 'bottom-4 left-4 max-w-sm',
    'bottom-right': 'bottom-4 right-4 max-w-sm'
  };

  const handleCustomSave = () => {
    saveCustomConsent(customConsent as Omit<CookieConsentData, 'timestamp' | 'version'>);
  };

  const categoryIcons = {
    necessary: Shield,
    analytics: BarChart,
    marketing: Target,
    preferences: Palette
  };

  if (compact && !showDetails) {
    return (
      <div className={`fixed ${positionClasses[position]} z-50 animate-in slide-in-from-bottom-2 duration-300`}>
        <Card className="shadow-lg border bg-background/95 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{t('cookies.banner.title')}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('cookies.banner.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={acceptAll}
                    size="sm"
                    className="h-8 px-3 text-xs"
                  >
                    {t('cookies.banner.acceptAll')}
                  </Button>
                  <Button 
                    onClick={acceptNecessary}
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                  >
                    {t('cookies.banner.acceptNecessary')}
                  </Button>
                  <Button 
                    onClick={() => setShowDetails(true)}
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs"
                  >
                    <Settings className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">{t('cookies.modal.title')}</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            {t('cookies.modal.description')}
          </p>

          <div className="space-y-4 mb-6">
            {Object.entries(cookieDescriptions).map(([key, desc]) => {
              const Icon = categoryIcons[key as keyof typeof categoryIcons];
              const isNecessary = key === 'necessary';
              const isEnabled = customConsent[key as keyof typeof customConsent];

              return (
                <div key={key} className="flex items-start gap-3 p-3 rounded-lg border">
                  <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{desc.title}</span>
                      {isNecessary && (
                        <Badge variant="secondary" className="text-xs">
                          {t('cookies.categories.necessary.required')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {desc.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {desc.examples.map((example, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {example}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Button
                      variant={isEnabled ? "default" : "outline"}
                      size="sm"
                      className="h-6 w-12 text-xs"
                      disabled={isNecessary}
                      onClick={() => {
                        if (!isNecessary) {
                          setCustomConsent(prev => ({
                            ...prev,
                            [key]: !prev[key as keyof typeof prev]
                          }));
                        }
                      }}
                    >
                      {isEnabled ? 'ON' : 'OFF'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={acceptAll} className="flex-1">
              {t('cookies.modal.acceptAll')}
            </Button>
            <Button onClick={handleCustomSave} variant="outline" className="flex-1">
              {t('cookies.modal.savePreferences')}
            </Button>
            <Button onClick={acceptNecessary} variant="ghost" className="flex-1">
              {t('cookies.modal.acceptNecessary')}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-4 text-center">
            {t('cookies.modal.notice')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
